import {useMemo} from 'react'
import {isRetargetableLine, groupDiffLinesWithoutBlanks} from '../helpers/line-helpers'
import type {DiffLine} from '../types'
import {useGetSelectedDiffLines, useGetSelectedLineRange} from '../stores/selectors'
import {useAllLinesAreRetargetable} from './use-all-lines-are-retargetable'

export interface StartConversationInfo {
  /**
   * The line number to use for the end of the conversation (or single line).
   * Already accounts for retargeting logic. Never undefined.
   */
  lineNumber: number

  /**
   * The line number to use for the start of a multi-line conversation.
   * Undefined for single-line comments.
   * Already accounts for retargeting logic.
   */
  startLineNumber: number | undefined

  /**
   * The side for the end of the conversation: 'left' or 'right'.
   * Takes into account retargeting rules for context/injected/empty lines.
   */
  side: 'left' | 'right'

  /**
   * The side for the start of a multi-line conversation: 'left' or 'right'.
   * Undefined for single-line comments.
   * Takes into account retargeting rules for context/injected/empty lines.
   */
  startSide: 'left' | 'right' | undefined

  /**
   * Whether this is a multiline comment.
   */
  isMultiline: boolean

  /**
   * Whether all selected lines can be retargeted to the right side.
   * True if all lines are context, injected context, or empty.
   */
  allLinesAreRetargetable: boolean
}

interface UseStartConversationInfoParams {
  /**
   * The current diff line being interacted with
   */
  line: DiffLine

  /**
   * Whether the current cell is on the left side of a split diff
   */
  isLeftSide: boolean

  /**
   * Whether the current row (and potentially other rows) are selected
   */
  isRowSelected: boolean
}

/**
 * Hook to compute start conversation information with retargeting logic.
 * This ensures ContentCell and StartConversationContextMenuItems use the same logic
 * for determining which side and line numbers to use when starting a conversation.
 *
 * Retargeting rules:
 * - Context, injected context, and empty lines default to right side
 * - If all selected lines are retargetable, force right side
 * - If there's a mix of retargetable and non-retargetable lines, use the actual side
 */
export function useStartConversationInfo({
  line,
  isLeftSide,
  isRowSelected,
}: UseStartConversationInfoParams): StartConversationInfo {
  const getSelectedDiffLines = useGetSelectedDiffLines()
  const getSelectedLineRange = useGetSelectedLineRange()

  const allLinesAreRetargetable = useAllLinesAreRetargetable({
    line,
    isRowSelected,
  })

  const lineNumber = useMemo(() => {
    // If not left side or no rows selected, return the current line's blob line number
    if (!isRowSelected) return line.blobLineNumber

    const selectedLineRange = getSelectedLineRange()
    if (!selectedLineRange) return line.blobLineNumber
    if (!isLeftSide) return selectedLineRange.endLineNumber

    // If there is a mix of retargetable and non-retargetable lines, we can't retarget to the right side, so return the
    // value from the selection
    if (!allLinesAreRetargetable) {
      return selectedLineRange.endLineNumber ?? line.blobLineNumber
    }

    const selectedLines = getSelectedDiffLines()
    const leftLines = groupDiffLinesWithoutBlanks(selectedLines).leftLines
    const lastSelectedLine = leftLines[leftLines.length - 1]

    // Otherwise, return the blob line number which is the right side value for context/injected/empty lines
    return lastSelectedLine?.blobLineNumber ?? line.blobLineNumber
  }, [allLinesAreRetargetable, getSelectedDiffLines, getSelectedLineRange, isLeftSide, isRowSelected, line])

  const startLineNumber = useMemo(() => {
    if (!isRowSelected) return undefined

    const selectedLineRange = getSelectedLineRange()
    if (!selectedLineRange) return undefined
    if (!isLeftSide) return selectedLineRange.startLineNumber

    if (!allLinesAreRetargetable) {
      return selectedLineRange?.startLineNumber
    }

    // Otherwise, return the blob line number which is the right side value for context/injected/empty lines
    const selectedLines = getSelectedDiffLines()
    const leftLines = groupDiffLinesWithoutBlanks(selectedLines).leftLines
    const firstSelectedLeftLine = leftLines[0]
    return firstSelectedLeftLine?.blobLineNumber
  }, [allLinesAreRetargetable, getSelectedDiffLines, getSelectedLineRange, isLeftSide, isRowSelected])

  // Determine which side to use for starting conversations.
  // For retargetable lines (context/injected/empty), we force right side unless there's a multi-line selection with non-retargetable lines.
  const conversationIsLeftSide = useMemo(() => {
    // If the line is not retargetable, use the actual side
    if (!line || !isRetargetableLine(line)) return isLeftSide

    // If the line is retargetable and we have no selection, default to right side
    if (!isRowSelected) return false

    // If all selected lines are retargetable, force right side
    if (allLinesAreRetargetable) return false

    const selectedLineRange = getSelectedLineRange()

    // If there's a mix of retargetable and non-retargetable lines, use the actual side of the start of the selection
    return selectedLineRange ? selectedLineRange.endOrientation === 'left' : isLeftSide
  }, [allLinesAreRetargetable, getSelectedLineRange, isLeftSide, isRowSelected, line])

  const conversationIsStartLeftSide = useMemo(() => {
    if (!isRowSelected) return isLeftSide

    if (allLinesAreRetargetable) return false

    const selectedLineRange = getSelectedLineRange()

    return selectedLineRange ? selectedLineRange.startOrientation === 'left' : isLeftSide
  }, [isRowSelected, isLeftSide, getSelectedLineRange, allLinesAreRetargetable])

  const isMultiline = useMemo(() => {
    return !!startLineNumber && startLineNumber !== lineNumber
  }, [startLineNumber, lineNumber])

  return {
    lineNumber,
    startLineNumber: startLineNumber ?? undefined,
    side: conversationIsLeftSide ? 'left' : 'right',
    startSide: isMultiline ? (conversationIsStartLeftSide ? 'left' : 'right') : undefined,
    isMultiline,
    allLinesAreRetargetable,
  }
}
